import "react-phone-number-input/style.css";

import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

import vaccineStyle from "components/AddVaccineForm/AddVaccineForm.module.css";
import { FormField } from "components/form/FormField/FormField";

import s from "./IntlPhoneField.module.css";

/**
 * True once there is at least one national subscriber digit (not only + / country code).
 * Handles incomplete E.164 while typing (e.g. "+353 4" before libphonenumber accepts a parse).
 */
const hasNationalSubscriberDigits = (e164, countryCode) => {
  const v = typeof e164 === "string" ? e164.trim() : "";
  if (!v || !countryCode) return false;

  const parsedIntl = parsePhoneNumberFromString(v);
  if (parsedIntl) {
    const n = String(parsedIntl.nationalNumber ?? "").replace(/\D/g, "");
    if (n.length > 0) return true;
  }

  let cc;
  try {
    cc = getCountryCallingCode(countryCode);
  } catch {
    return false;
  }

  const digits = v.replace(/\D/g, "");
  if (digits.startsWith(cc)) {
    return digits.slice(cc.length).length > 0;
  }

  const parsedNational = parsePhoneNumberFromString(v, countryCode);
  if (parsedNational) {
    return (
      String(parsedNational.nationalNumber ?? "").replace(/\D/g, "").length > 0
    );
  }

  return false;
};

/**
 * International phone field (country select + E.164 in form state).
 * Uses react-phone-number-input / libphonenumber-js.
 */
export function IntlPhoneField({
  name,
  label,
  placeholder = "",
  ariaRequired = false,
  defaultCountry = "IE",
}) {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext();

  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);

  useEffect(() => {
    setSelectedCountry(defaultCountry);
  }, [defaultCountry]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const parsedCountry = parsePhoneNumberFromString(
          String(field.value ?? "").trim() || ""
        )?.country;
        const countryForDigits = parsedCountry ?? selectedCountry;
        const showAsPlaceholderTone = !hasNationalSubscriberDigits(
          field.value,
          countryForDigits
        );
        const hadError = Boolean(errors[name]);
        return (
          <FormField name={name}>
            <label className={vaccineStyle.pregnancyFormLabel} htmlFor={name}>
              {label}
              {ariaRequired ? " *" : ""}
            </label>
            <div
              className={`intlPhoneFieldWrap ${s.shell} ${
                showAsPlaceholderTone ? s.shellMuted : ""
              }`.trim()}
            >
              <PhoneInput
                ref={field.ref}
                international
                defaultCountry={defaultCountry}
                placeholder={placeholder}
                countryCallingCodeEditable={false}
                limitMaxLength
                onCountryChange={(c) => {
                  setSelectedCountry(c ?? defaultCountry);
                }}
                value={field.value || undefined}
                onChange={(v) => {
                  field.onChange(v ?? "");
                  if (hadError) {
                    void trigger(name);
                  }
                }}
                onBlur={(e) => {
                  field.onBlur(e);
                  void trigger(name);
                }}
                numberInputProps={{
                  id: name,
                  name,
                  className: "MuiOutlinedInput-input",
                  inputMode: "tel",
                  autoComplete: "tel",
                  "aria-required": ariaRequired ? "true" : undefined,
                }}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
}
